/**
 * Standalone drag-to-scroll custom element.
 *
 * Wraps any native scrollable container and adds mouse/pointer drag. The scroll
 * container is the first child with `[data-scroller]` (falls back to first child).
 * Auto-detects scroll axis. Sets a `dragging` attribute on itself while a drag
 * is active — use for CSS cursors.
 *
 * Usage:
 *   <drag-scroller>
 *     <div data-scroller style="overflow-x:auto; display:flex;">
 *       <div>slide</div>
 *       <div>slide</div>
 *     </div>
 *   </drag-scroller>
 *
 * Attributes:
 *   disabled="true" — disables drag interaction
 */
class DragScroller extends HTMLElement {
  constructor() {
    super();
    this._dragging = false;
    this._handleMouseDown = this._handleMouseDown.bind(this);
  }

  connectedCallback() {
    this.scroller = this.querySelector('[data-scroller]') || this.firstElementChild;
    console.log('[drag-scroller] connectedCallback', {
      host: this,
      scroller: this.scroller,
      scrollWidth: this.scroller?.scrollWidth,
      clientWidth: this.scroller?.clientWidth,
      overflowsX: this.scroller ? this.scroller.scrollWidth > this.scroller.clientWidth : null,
    });
    if (!this.scroller) {
      console.warn('[drag-scroller] no scroller child found — aborting');
      return;
    }
    this.scroller.addEventListener('mousedown', this._handleMouseDown);
    console.log('[drag-scroller] mousedown listener attached');
  }

  disconnectedCallback() {
    if (!this.scroller) return;
    this.scroller.removeEventListener('mousedown', this._handleMouseDown);
  }

  get disabled() {
    return this.getAttribute('disabled') === 'true';
  }

  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }

  _getAxis() {
    const el = this.scroller;
    if (el.scrollHeight > el.clientHeight && el.scrollWidth === el.clientWidth) return 'y';
    if (el.scrollWidth > el.clientWidth && el.scrollHeight === el.clientHeight) return 'x';
    return el.scrollWidth > el.scrollHeight ? 'x' : 'y';
  }

  _handleMouseDown(event) {
    console.log('[drag-scroller] mousedown fired', { target: event.target, button: event.button });
    if (this.disabled || this._dragging) {
      console.log('[drag-scroller] abort mousedown — disabled/already dragging', { disabled: this.disabled, dragging: this._dragging });
      return;
    }
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('model-viewer')) {
      console.log('[drag-scroller] abort — inside model-viewer');
      return;
    }

    event.preventDefault();

    const axis = this._getAxis();
    const scroller = this.scroller;
    const startPosition = event[axis];
    console.log('[drag-scroller] drag start', { axis, startPosition, scrollLeft: scroller.scrollLeft });

    const controller = new AbortController();
    const { signal } = controller;
    let previous = startPosition;
    let moved = false;

    this._dragging = true;

    const preventClick = (e) => e.preventDefault();

    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;
      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        console.log('[drag-scroller] drag moved — first move', { initialDelta, current });
        try { this.setPointerCapture(event.pointerId); } catch (e) {}

        // Suppress the click event that would otherwise fire after drag
        document.addEventListener('click', preventClick, { once: true, capture: true, signal });

        // Let a parent drag-scroller take over if this one cannot scroll further
        const closestParent = this.parentElement && this.parentElement.closest('drag-scroller');
        const isNested = closestParent instanceof DragScroller && closestParent !== this;
        const atStart = scroller.scrollLeft === 0 && scroller.scrollTop === 0;
        const atEnd = axis === 'x'
          ? scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth
          : scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight;
        const cannotMove = (initialDelta < 0 && atStart) || (initialDelta > 0 && atEnd);
        console.log('[drag-scroller] boundary check', { isNested, atStart, atEnd, cannotMove });
        if (isNested && cannotMove) {
          console.log('[drag-scroller] aborting — nested & cannot move');
          controller.abort();
          return;
        }

        this.setAttribute('dragging', '');
      }

      event.stopImmediatePropagation();
      const delta = previous - current;
      previous = current;
      scroller.scrollBy({ [axis === 'x' ? 'left' : 'top']: delta, behavior: 'instant' });
    };

    const onPointerUp = (event) => {
      controller.abort();
      this._dragging = false;
      this.removeAttribute('dragging');
      if (this.hasPointerCapture && this.hasPointerCapture(event.pointerId)) {
        this.releasePointerCapture(event.pointerId);
      }
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  }
}

if (!customElements.get('drag-scroller')) {
  customElements.define('drag-scroller', DragScroller);
}
